import { ReactNode } from 'react';

import {
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraText,
  Check,
  DatePicker,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  LightningBolt,
} from '@equip.health/ui';

import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import { TaskStatusValue, taskStatusDefinitions } from '~/lib/util/task.util';

const { critical, neutral } = EquipUIFireflyV2Theme.colors;
const { label, labelBold } = EquipUITheme.typography;
const TOP_LEVEL_ID = 'task-detail-actions';

export type ActionsSectionProps = {
  closedReason?: string;
  currentDueDate: string;
  date: string;
  hasEditFamilyTaskPermission: boolean;
  isEditing: boolean;
  isFamilyTask: boolean;
  isNonEditable: boolean;
  onClosedReasonChangeClick: () => void;
  onDateChange: (date: Date) => void;
  onStatusChange: (status: TaskStatusValue) => void;
  taskStatusId: string;
  validateDueDate: (isValid: boolean) => void;
};

const ActionsSection = ({
  closedReason,
  currentDueDate,
  date,
  hasEditFamilyTaskPermission,
  isEditing,
  isFamilyTask,
  isNonEditable,
  onClosedReasonChangeClick,
  onDateChange,
  onStatusChange,
  taskStatusId,
  validateDueDate,
}: ActionsSectionProps) => {
  const { editTaskError, resetEditTaskError, isEditTaskLoading } =
    useTaskContext();
  const { trackEvent } = useAnalytics();
  const changeStatusMapper: Record<
    TaskStatusValue,
    {
      ctaIcon: ReactNode;
      ctaLabel: string;
      onChange: (status: TaskStatusValue) => void;
    }
  > = {
    [TaskStatusValue.TO_DO]: {
      ctaIcon: <LightningBolt />,
      ctaLabel: 'Start task',
      onChange: () => {
        trackEvent(PatientEvents.startTaskTaskTabButtonClicked, {
          Source: AnalyticsSourceValues.TASKS_TAB,
        });
        resetEditTaskError();
        onStatusChange(TaskStatusValue.IN_PROGRESS);
      },
    },
    [TaskStatusValue.IN_PROGRESS]: {
      ctaIcon: <Check fill="white" width="16px" />,
      ctaLabel: 'Complete task',
      onChange: () => {
        trackEvent(PatientEvents.completeTaskTaskTabButtonClicked, {
          Source: AnalyticsSourceValues.TASKS_TAB,
        });
        resetEditTaskError();
        onStatusChange(TaskStatusValue.DONE);
      },
    },
    [TaskStatusValue.DONE]: null,
    [TaskStatusValue.VOID]: null,
    [TaskStatusValue.CLOSED]: null,
  };

  const taskStatusValue = Number.parseInt(taskStatusId, 10) - 1;

  const { color, textColor, title } =
    taskStatusDefinitions[taskStatusValue] ?? {};

  const { ctaIcon, ctaLabel, onChange } =
    changeStatusMapper[taskStatusValue] ?? {};

  const isEditDisabledForFamilyTask =
    isFamilyTask && !hasEditFamilyTaskPermission;

  return (
    <ChakraGridItem borderTop={`2px solid ${neutral.line}`}>
      <ChakraFlex align="center" justify="space-between" padding="34px 0">
        <ChakraBox width="250px">
          <DatePicker
            background="transparent"
            color={critical.base}
            data-testid={`${TOP_LEVEL_ID}__due-date`}
            fillOpacity={1}
            id={`${TOP_LEVEL_ID}__due-date`}
            initialValue={currentDueDate}
            isDisabled={isNonEditable || isFamilyTask}
            minDate={new Date()}
            onSelect={onDateChange}
            onValidateDate={validateDueDate}
            value={date}
          />
        </ChakraBox>
        {ctaLabel && (
          <ChakraFlex alignItems="center" justifyContent="space-between">
            {!isEditing && editTaskError && (
              <ChakraText
                {...label}
                color="critical.base"
                id={`${TOP_LEVEL_ID}__edit-task-error-text`}
                marginRight="16px"
              >
                {editTaskError}
              </ChakraText>
            )}
            <Button
              fontSize="12px"
              id={`${TOP_LEVEL_ID}__${ctaLabel}`}
              isDisabled={
                isEditing || isEditTaskLoading || isEditDisabledForFamilyTask
              }
              onClick={onChange}
              rightIcon={ctaIcon}
            >
              {ctaLabel}
            </Button>
          </ChakraFlex>
        )}
        {[TaskStatusValue.CLOSED, TaskStatusValue.VOID].includes(
          taskStatusValue,
        ) && (
          <ChakraFlex
            {...labelBold}
            align="center"
            background={color}
            borderRadius="26px"
            color={textColor}
            height="29px"
            lineHeight="16.8px"
            minWidth="max-content"
            padding="6px 12px"
          >
            {title && (
              <ChakraText id={`${TOP_LEVEL_ID}__task-status__title`}>
                {title ?? ''}
              </ChakraText>
            )}
            {closedReason && (
              <>
                <ChakraText
                  fontWeight="400"
                  id={`${TOP_LEVEL_ID}__task-status__closed-or-void-reason__title`}
                  paddingX="8px"
                >
                  {closedReason}
                </ChakraText>
                <ChakraText
                  _hover={{ cursor: 'pointer' }}
                  color="primary.100"
                  id={`${TOP_LEVEL_ID}__task-status__closed-or-void-reason__change-button`}
                  onClick={onClosedReasonChangeClick}
                >
                  Change
                </ChakraText>
              </>
            )}
          </ChakraFlex>
        )}
      </ChakraFlex>
    </ChakraGridItem>
  );
};

export default ActionsSection;
